import React from "react";
import PropTypes from "prop-types";
import {Box, makeStyles, Typography, useTheme} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import ReservationOptionItem from "./ReservationOptionItem";
import {useDate, useI18N} from "../../../common/common";
import {getDateLabel} from "../../../selectors/dates";
import { black_circle, square } from "../../../common/constant";
import { retrieveContentfulImage } from "../../../common/image";
import { useBreakpoints } from "../../../hooks";
import WeatherNightClear from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/weather/weather/weather-night-clear.svg";
import { StreamlineIcon } from "@hapinest/theme";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
    apartmentImage: {
        display: "block",
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    nightsIcon: {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.white
    },
    bold: {
        fontWeight: "700"
    },
    redColorText: {
        color: theme.palette.error.danger
    }
}));

const ApartmentReservationOptions = ({
    apartment,
    apartmentFlex,
    dateFrom,
    dateTo,
    nights,
    onReserveOption,
    reserveOptionDisabled,
    isTrialMember,
    resort
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const {i18n, i18nLowerIfRequired} = useI18N();
    const {formatDate} = useDate();
    const {isExtraSmallScreen} = useBreakpoints();

    const handleReserveApartment = (isFlexOption) => {
        const selectedApartment = isFlexOption ? apartmentFlex : apartment;
        onReserveOption(selectedApartment, isFlexOption);
    };

    const createNonBreakingSpaceDates = (str) => {
        return (
            <span
                key={str}
                className={classes.bold}
            >
                {str}
            </span>
        );
    };

    const boldWithColor = (str) => {
        return (
            <span className={clsx([classes.bold, classes.redColorText])}>{str}</span>
        );
    };

    const bulletListItem = (text) => {
        return (
            <Box
                display="flex"
            >
                <Box
                    mx={1}
                >
                    {black_circle}
                </Box>

                <Box
                    flex={1}
                    className={classes.ellipsis}
                >
                    <Typography
                        variant="body1"
                        className={classes.ellipsis}
                    >
                        {text}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Typography
                variant="h5"
                align="center"
            >
                {apartment ? (
                    <>
                        {apartment.apartment_size_name + " " + apartment.apartment_name + ", " + apartment.apartment_size + " " + square + ", " + i18n("view.apartment_card.max_guest_number", {numOfGuests: apartment.max_number_of_guests, plusExtraBed: "no"}) + " " + i18nLowerIfRequired("dialog.search_bar.search_date_guest.guests")}
                    </>
                ) : <Skeleton/>}
            </Typography>

            <Box
                mt={isExtraSmallScreen() ? 3 : 5}
                mb={2}
                borderRadius={theme.spacing(1)}
                overflow="hidden"
            >
                {apartment ? (
                    <img
                        src={retrieveContentfulImage(apartment?.apartment_picture, { width: isExtraSmallScreen() ? 552 : 396, height: 180, fit: "fill" })}
                        alt={apartment?.apartment_name}
                        className={classes.apartmentImage}
                    />
                ) : (
                    <Skeleton
                        variant="rect"
                        width={isExtraSmallScreen() ? 552 : 396}
                        height={180}
                    />
                )}
            </Box>

            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                flexWrap="nowrap"
            >
                <Box
                    flexGrow={1}
                    pb={2.5}
                    pr={2}
                    borderBottom={"1px solid " + theme.palette.secondary.main}
                >
                    <Box
                        mb={2}
                        color={theme.palette.secondary["900"]}
                    >
                        <Typography
                            variant="overline"
                            color="inherit"
                        >
                            {i18n("view.resort.good_to_know.check_in")}
                        </Typography>
                    </Box>


                    <Typography
                        variant="subtitle1"
                    >
                        {formatDate(dateFrom, {weekday: "short", day: "numeric", month: "short", year: "numeric"})}
                    </Typography>

                </Box>
                <Box
                    display="flex"
                >
                    <Box
                        p={2}
                        color={theme.palette.text.white}
                        style={{ backgroundColor: theme.palette.secondary["900"]}}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <StreamlineIcon
                            className={classes.nightsIcon}
                        >
                            <WeatherNightClear/>
                        </StreamlineIcon>
                        <Typography
                            variant="overline"
                            color="inherit"
                            align="center"
                        >
                            {`${nights} ${nights > 1 ? i18n("common.label.nights") : i18n("common.label.night")}`}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    flexGrow={1}
                    pb={2.5}
                    pl={2}
                    borderBottom={"1px solid " + theme.palette.secondary.main}
                >
                    <Box
                        mb={2}
                        color={theme.palette.secondary["900"]}
                        textAlign="right"
                    >
                        <Typography
                            variant="overline"
                            color="inherit"
                        >
                            {i18n("view.resort.good_to_know.check_out")}
                        </Typography>
                    </Box>

                    <Typography
                        variant="subtitle1"
                        align="right"
                    >
                        {formatDate(dateTo, {weekday: "short", day: "numeric", month: "short", year: "numeric"})}
                    </Typography>
                </Box>
            </Box>

            <Box
                mt={isExtraSmallScreen() ? 3 : 5}
            >
                <Box
                    mt={3}
                >
                    <ReservationOptionItem
                        title={resort?.isPartner ? i18n("view.apartment_card.cancellation_conditions") : i18n("view.apartment_card.regular_option")}
                        subTitle={
                            <>
                                {
                                    bulletListItem(isTrialMember ? i18n("view.apartment_card.regular_conditions_trial_member") : i18n("view.apartment_card.free_cancellation_until",
                                        {
                                            nonBreakingSpaceDate: createNonBreakingSpaceDates,
                                            formattedDate: getDateLabel(apartment?.cancellation_policies?.regular?.free_until, "", formatDate)
                                        }))
                                }
                                {
                                    resort?.isPartner && (
                                        bulletListItem(i18n("view.apartment_card.attention_cancellations",
                                            {
                                                boldWithColor: boldWithColor,
                                                bold: createNonBreakingSpaceDates,
                                                formattedDate: getDateLabel(apartment?.cancellation_policies?.regular?.free_until, "", formatDate)
                                            }))
                                    )
                                }
                                {
                                    apartment?.extra_bed ? bulletListItem(i18n("view.apartment_card.extra_bed")) : ""
                                }
                            </>
                        }
                        apartment={apartment}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        buttonText={i18n("common.label.reserve")}
                        onClickButton={() => handleReserveApartment(false)}
                        buttonDisabled={reserveOptionDisabled && !apartment?.isLoading}
                    />
                </Box>

                {apartmentFlex !== null && (
                    <Box
                        mt={1.5}
                    >
                        <ReservationOptionItem
                            title={i18n("view.apartment_card.flex_option")}
                            subTitle={
                                <>
                                    {
                                        bulletListItem(i18n("view.apartment_card.free_cancellation_until",
                                            {
                                                nonBreakingSpaceDate: createNonBreakingSpaceDates,
                                                formattedDate: getDateLabel(apartmentFlex?.cancellation_policies?.flex?.free_until, "", formatDate)
                                            }))
                                    }
                                    {
                                        apartment?.extra_bed ? bulletListItem(i18n("view.apartment_card.extra_bed")) : ""
                                    }
                                </>
                            }
                            apartment={apartmentFlex}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            buttonText={i18n("common.label.reserve")}
                            onClickButton={() => handleReserveApartment(true)}
                            buttonDisabled={reserveOptionDisabled && !apartmentFlex?.isLoading}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

ApartmentReservationOptions.propTypes = {
    apartment: PropTypes.object,
    apartmentFlex: PropTypes.object,
    dateFrom: PropTypes.any,
    dateTo: PropTypes.any,
    nights: PropTypes.number,
    onReserveOption: PropTypes.func,
    reserveOptionDisabled: PropTypes.bool,
    isTrialMember: PropTypes.bool,
    resort: PropTypes.object
};

export default ApartmentReservationOptions;
